/**
 * TermsPage.tsx — static Terms of Service.
 *
 * Seven sections: agreement, accounts, acceptable use, billing, intellectual
 * property, disclaimers, termination, changes.
 */
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const SECTIONS = [
  {
    title: "1. Agreement to terms",
    body: [
      "By accessing or using the OUTRENA Service, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, you may not access or use the Service.",
      "These Terms constitute a legally binding agreement between you (or the entity you represent) and OUTRENA. You affirm that you are at least 18 years of age and have the authority to bind your entity.",
    ],
  },
  {
    title: "2. Accounts & tenant isolation",
    body: [
      "When you create an OUTRENA tenant, you become the tenant owner and are responsible for all activity under your account, including invites, role assignments, and content generated by your team.",
      "Each tenant is isolated at the database schema layer. You are responsible for maintaining the confidentiality of your credentials and for notifying us immediately of any unauthorized access.",
      "We reserve the right to suspend or terminate any account that violates these Terms or that we reasonably believe is engaging in fraudulent, abusive, or illegal activity.",
    ],
  },
  {
    title: "3. Acceptable use",
    body: [
      "You agree not to: (a) use the Service to send unsolicited commercial email in violation of CAN-SPAM, GDPR, or any other applicable email regulation; (b) upload or process data you do not have the right to use; (c) attempt to access other tenants' data; (d) reverse engineer, decompile, or otherwise attempt to extract source code; (e) use the Service to build a competing product.",
      "OUTRENA enforces rate limits, sending caps, and content QA to maintain deliverability and compliance. We may modify these limits with reasonable notice.",
    ],
  },
  {
    title: "4. Billing & subscriptions",
    body: [
      "Paid plans are billed in advance on a recurring monthly or yearly basis. All fees are non-refundable except where required by law.",
      "You can upgrade, downgrade, or cancel at any time. Downgrades and cancellations take effect at the end of the current billing period. Upgrades take effect immediately and are prorated.",
      "We may change our fees with at least 30 days' notice. Fee changes do not apply to your current billing period.",
      "Late or failed payments may result in account suspension after 7 days. We will notify you before suspending access.",
    ],
  },
  {
    title: "5. Intellectual property",
    body: [
      "You retain all rights to the content you submit to the Service (prospect lists, campaigns, sequences, message copy, etc.). We license from you the limited right to process that content solely to provide the Service to you.",
      "OUTRENA retains all rights to the Service itself, including the software, AI models, prompt templates, design, and branding. No rights are granted to you except as expressly stated in these Terms.",
      "Aggregate, anonymized insights derived from usage across tenants (e.g. benchmark reply rates by industry) may be used by OUTRENA to improve the Service, provided they cannot be reverse-engineered to identify any tenant.",
    ],
  },
  {
    title: "6. Disclaimers & limitation of liability",
    body: [
      "The Service is provided 'as is' and 'as available' without warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, error-free, or that AI-generated content will be accurate or appropriate for your use case.",
      "To the maximum extent permitted by law, OUTRENA shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, arising from your use of the Service.",
      "Our total liability for any claim arising from these Terms or the Service shall not exceed the amount you paid us in the 12 months preceding the claim.",
    ],
  },
  {
    title: "7. Termination",
    body: [
      "You may terminate your account at any time from Setup → Billing. Upon termination, your data will be retained for 30 days to allow reactivation, then permanently deleted within an additional 30 days.",
      "We may terminate or suspend your access if you materially breach these Terms and fail to cure within 14 days of written notice.",
      "Provisions that by their nature should survive termination (including intellectual property, disclaimers, liability, and governing law) shall continue in effect.",
    ],
  },
  {
    title: "8. Changes to these Terms",
    body: [
      "We may modify these Terms from time to time. We will notify you of material changes by email and in-app at least 30 days before they take effect.",
      "Your continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree, you may cancel your subscription before the changes take effect.",
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.35, ease: "easeOut" as const },
};

export function TermsPage() {
  return (
    <div>
      <section className="border-b bg-card">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              Legal
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Terms of Service
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              Last updated: {new Date().getFullYear()}. These Terms govern your
              use of the OUTRENA platform.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {SECTIONS.map((section, i) => (
            <motion.div key={section.title} {...fadeUp} transition={{ duration: 0.35, delay: i * 0.03 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                  <CardDescription>
                    {section.body.length} paragraph
                    {section.body.length === 1 ? "" : "s"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                  {section.body.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
